import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const activeConversationIdRef = useRef(null);

  const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('adminToken');
      const newSocket = io(SOCKET_URL, {
        auth: { token },
        transports: ['websocket', 'polling']
      });

      newSocket.on('connect_error', () => {
        setSocket(null);
      });

      newSocket.on('connect', () => {
        if (activeConversationIdRef.current) {
          newSocket.emit('join_conversation', activeConversationIdRef.current);
        }
      });

      newSocket.on('new_message', (message) => {
        setMessages((prevMessages) => [...prevMessages, message]);
      });

      newSocket.on('user_typing', (data) => {
        if (activeConversationIdRef.current && activeConversationIdRef.current === data.conversationId) {
          setIsTyping(data.isTyping);
        }
      });

      setSocket(newSocket);

      return () => {
        newSocket.disconnect();
      };
    }
  }, [user]);

  const joinConversation = useCallback((conversation) => {
    if (socket && conversation) {
      activeConversationIdRef.current = conversation.id;
      socket.emit('join_conversation', conversation.id);
      setActiveConversation(conversation);
    }
  }, [socket]);

  const leaveConversation = useCallback((conversationId) => {
    if (socket && conversationId) {
      socket.emit('leave_conversation', conversationId);
      if (activeConversationIdRef.current === conversationId) {
        activeConversationIdRef.current = null;
      }
      setActiveConversation(null);
      setMessages([]);
    }
  }, [socket]);

  const sendTyping = useCallback((isTyping) => {
    if (socket && activeConversationIdRef.current) {
      socket.emit('typing', {
        conversationId: activeConversationIdRef.current,
        isTyping
      });
    }
  }, [socket]);

  return (
    <ChatContext.Provider value={{
      socket,
      conversations,
      setConversations,
      activeConversation,
      setActiveConversation,
      messages,
      setMessages,
      joinConversation,
      leaveConversation,
      sendTyping,
      isTyping
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);
